import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../contexts/AuthContext'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import {
  getBetHistory,
  getEventDetail,
  listOpenEvents,
  placeBet,
  type PredictionBetSlip,
  type PredictionEvent,
  type PredictionPaymentType,
  type PredictionSelection,
} from '../services/predictionService'
import PredictionDisplayBoard from '../components/prediction/PredictionDisplayBoard'
import PredictionRewardInfoCard from '../components/prediction/PredictionRewardInfoCard'
import '../components/prediction/predictionModern.css'

const formatNumber = (value: number) => Number(value || 0).toLocaleString('th-TH')

const getCurrentPoints = (userInfo: ReturnType<typeof useUserInfo>['userInfo']): number => {
  if (!userInfo) return 0
  const loyaltyPoints = Number(userInfo.loyaltyPoints?.currentPoints)
  if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
  return 0
}

const PredictionPage: React.FC = () => {
  const { themeConfig } = useTenantThemeConfig()
  const { user } = useAuth()
  const { walletData, formatCurrency, refreshWallet } = useWallet()
  const { userInfo, refreshUserInfo } = useUserInfo()

  const [events, setEvents] = useState<PredictionEvent[]>([])
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PredictionEvent | null>(null)
  const [history, setHistory] = useState<PredictionBetSlip[]>([])
  const [selectedSelectionId, setSelectedSelectionId] = useState('')
  const [stake, setStake] = useState('100')
  const [paymentType, setPaymentType] = useState<PredictionPaymentType>('cash')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tab, setTab] = useState<'bet' | 'history'>('bet')

  const points = getCurrentPoints(userInfo)
  const balanceText = walletData ? formatCurrency(walletData.balance.main) : '0.00'

  const allowedPaymentTypes = useMemo(
    () => detail?.allowedPaymentTypes || ['cash', 'points'],
    [detail?.allowedPaymentTypes]
  )

  const loadEvents = useCallback(async (forceRefresh = false) => {
    setLoading(true)
    try {
      const res = await listOpenEvents(forceRefresh)
      const list = res.data || []
      setEvents(list)
      setSelectedEventId((prev) => {
        if (list.length === 0) return null
        if (prev && list.some((event) => event._id === prev)) return prev
        return list[0]?._id ?? null
      })
      if (list.length === 0) {
        setDetail(null)
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดรายการไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const onUpdated = () => {
      void loadEvents(true)
    }
    window.addEventListener('prediction_events_updated', onUpdated)
    return () => window.removeEventListener('prediction_events_updated', onUpdated)
  }, [loadEvents])

  const loadDetail = useCallback(async (eventId: string) => {
    try {
      const res = await getEventDetail(eventId)
      setDetail(res.data)
      if (res.data.allowedPaymentTypes?.length) {
        setPaymentType(res.data.allowedPaymentTypes[0])
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดรายละเอียดไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    }
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      const res = await getBetHistory({ limit: 50 })
      setHistory(res.data || [])
    } catch {
      setHistory([])
    }
  }, [])

  useEffect(() => {
    void loadEvents()
    void loadHistory()
  }, [loadEvents, loadHistory])

  useEffect(() => {
    if (selectedEventId) {
      void loadDetail(selectedEventId)
    }
  }, [selectedEventId, loadDetail])

  useEffect(() => {
    if (!selectedEventId || tab !== 'bet') return undefined
    const timer = window.setInterval(() => {
      void loadDetail(selectedEventId)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [selectedEventId, tab, loadDetail])

  const selectedSelection = useMemo(
    () => detail?.selections?.find((s) => s._id === selectedSelectionId) || null,
    [detail?.selections, selectedSelectionId]
  )

  const stakeNum = Number(stake)
  const potentialPayout = selectedSelection && Number.isFinite(stakeNum)
    ? Math.round(stakeNum * selectedSelection.currentOdds * 100) / 100
    : 0

  const handlePlaceBet = async () => {
    if (!user || !detail || !selectedSelectionId) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาเลือกตัวเลือก', confirmButtonColor: '#f59e0b' })
      return
    }

    const amount = Number(stake)
    if (!Number.isFinite(amount) || amount <= 0) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาระบุยอดแทง', confirmButtonColor: '#f59e0b' })
      return
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการแทง',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.6">
          <div><b>งาน:</b> ${detail.title}</div>
          <div><b>เลือก:</b> ${selectedSelection?.label || '-'}</div>
          <div><b>ยอด:</b> ${formatNumber(amount)} ${paymentType === 'points' ? 'คะแนน' : 'บาท'}</div>
          <div><b>Odds:</b> ${selectedSelection?.currentOdds || '-'}</div>
          <div><b>จ่ายถ้าถูก:</b> ${formatNumber(potentialPayout)}</div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#16a34a',
    })

    if (!confirm.isConfirmed) return

    setSubmitting(true)
    try {
      const res = await placeBet({
        eventId: detail._id,
        selectionId: selectedSelectionId,
        stake: amount,
        paymentType,
      })

      await Swal.fire({
        icon: 'success',
        title: 'แทงสำเร็จ',
        text: `เลขที่โพย ${res.data.slip.slipId}`,
        confirmButtonColor: '#16a34a',
      })

      setSelectedSelectionId('')
      await Promise.all([loadDetail(detail._id), loadHistory(), refreshWallet(), refreshUserInfo()])
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'แทงไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const renderPopularity = (event: PredictionEvent) => {
    const popularity = event.popularity
    if (!popularity?.selections?.length) return null

    return (
      <PredictionDisplayBoard
        category={event.category}
        popularity={popularity}
        title={event.title}
        footerTitle={event.title}
        subtitle={`${event.eventCode} · realtime`}
        totalCandidates={popularity.selections.length}
        displayConfig={event.displayConfig}
        live
      />
    )
  }

  const renderSelectionCard = (sel: PredictionSelection) => {
    const isActive = selectedSelectionId === sel._id
    return (
      <button
        key={sel._id}
        type="button"
        onClick={() => setSelectedSelectionId(sel._id)}
        className={`pred-selection-btn${isActive ? ' pred-selection-btn--active' : ''}`}
        style={sel.barColor ? { borderLeftWidth: 4, borderLeftColor: sel.barColor } : undefined}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {sel.imageUrl && <img src={sel.imageUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />}
            <div>
              <div className="pred-selection-btn__label">{sel.badgeLabel ? `${sel.badgeLabel} ` : ''}{sel.label}</div>
              {sel.subtitle && <div className="pred-selection-btn__sub">{sel.subtitle}</div>}
            </div>
          </div>
          <div className="text-end">
            <div className="pred-selection-btn__odds">{sel.currentOdds}</div>
            <div className="pred-selection-btn__odds-label">odds</div>
          </div>
        </div>
      </button>
    )
  }

  if (themeConfig?.settings?.predictionEnabled !== true) {
    return (
      <div className="container py-5 pred-page">
        <div className="pred-disabled-card">
          <h5>ยังไม่เปิดใช้งานทายผล (Prediction)</h5>
          <p className="mb-3">
            ฟีเจอร์นี้ยังไม่ได้เปิดให้ tenant ของคุณ — ติดต่อผู้ดูแลระบบหากต้องการใช้งาน
          </p>
          <Link to="/dashboard" className="pred-submit d-inline-block text-decoration-none text-center" style={{ width: 'auto', padding: '10px 24px' }}>
            กลับหน้าหลัก
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-3 pred-page">
      <div className="pred-page__header">
        <div>
          <h4 className="pred-page__title">ทายผล Prediction</h4>
          <div className="pred-page__subtitle">
            ยอดเงิน {balanceText} · คะแนน {formatNumber(points)} pts
          </div>
        </div>
        <div className="pred-tabs">
          <button type="button" className={`pred-tabs__btn${tab === 'bet' ? ' pred-tabs__btn--active' : ''}`} onClick={() => setTab('bet')}>แทง</button>
          <button type="button" className={`pred-tabs__btn${tab === 'history' ? ' pred-tabs__btn--active' : ''}`} onClick={() => setTab('history')}>ประวัติ</button>
        </div>
      </div>

      {tab === 'history' ? (
        <div className="pred-card">
          {history.length === 0 ? (
            <div className="pred-empty">ยังไม่มีประวัติการแทง</div>
          ) : (
            history.map((slip) => (
              <div key={slip.slipId} className="pred-history-item">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <strong className="pred-history-item__title">{slip.eventTitle}</strong>
                  <span className="pred-badge">{slip.status}</span>
                </div>
                <div className="small mt-1">{slip.selectionLabel} · odds {slip.lockedOdds}</div>
                <div className="small">
                  แทง {formatNumber(slip.stake)} {slip.paymentType === 'points' ? 'pts' : 'บาท'}
                  {' · '}จ่าย {formatNumber(slip.payoutAmount ?? slip.potentialPayout)}
                </div>
                <div className="small" style={{ opacity: 0.75 }}>{new Date(slip.createdAt).toLocaleString('th-TH')} · {slip.slipId}</div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="pred-card h-100">
              <h6 className="pred-card__title">งานที่เปิดรับ</h6>
              {loading && <div className="pred-page__subtitle">กำลังโหลด...</div>}
              {events.map((event) => (
                <button
                  key={event._id}
                  type="button"
                  onClick={() => setSelectedEventId(event._id)}
                  className={`pred-event-btn${selectedEventId === event._id ? ' pred-event-btn--active' : ''}`}
                >
                  <div className="pred-event-btn__name">{event.title}</div>
                  <div className="pred-event-btn__meta">{event.eventCode} · {event.category}</div>
                </button>
              ))}
              {events.length === 0 && !loading && (
                <div className="pred-empty">ไม่มีรายการ</div>
              )}
            </div>
          </div>

          <div className="col-lg-8">
            {!detail ? (
              <div className="pred-card pred-empty">{events.length === 0 ? 'ไม่มีรายการ' : 'เลือกงานทางซ้าย'}</div>
            ) : (
              <div className="pred-card">
                <h5 className="pred-detail-title">{detail.title}</h5>
                {detail.description && <p className="pred-detail-desc">{detail.description}</p>}

                <PredictionRewardInfoCard
                  rewardProfile={detail.rewardProfile}
                  prizeInfo={detail.prizeInfo}
                />

                {renderPopularity(detail)}

                <h6 className="pred-card__title">เลือกตัวเลือก</h6>
                {(detail.selections || []).map(renderSelectionCard)}

                <div className="row g-2 mt-3">
                  <div className="col-md-4">
                    <label className="pred-form-label">ยอดแทง</label>
                    <input
                      type="number"
                      className="pred-input"
                      value={stake}
                      onChange={(e) => setStake(e.target.value)}
                      min={detail.minStake || 1}
                      max={detail.maxStake || undefined}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="pred-form-label">ชำระด้วย</label>
                    <select
                      className="pred-select"
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value as PredictionPaymentType)}
                    >
                      {allowedPaymentTypes.includes('cash') && <option value="cash">เงินสด</option>}
                      {allowedPaymentTypes.includes('points') && <option value="points">คะแนน</option>}
                    </select>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="pred-payout w-100">
                      จ่ายถ้าถูก: <strong>{formatNumber(potentialPayout)}</strong>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="pred-submit"
                  disabled={submitting || !selectedSelectionId}
                  onClick={() => void handlePlaceBet()}
                >
                  {submitting ? 'กำลังส่งโพย...' : 'ยืนยันแทง'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PredictionPage
